import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFrame from '@/components/MobileFrame';
import { Icon, type IconName } from '@/components/Icons';
import { tpl, useT } from '@/lib/i18n';
import { useOnboardingStore, type UserType } from '@/lib/store';
import { setUserType as apiSetUserType } from '@/lib/onboarding-api';

export default function UserTypeScreen() {
    const t = useT();
    const navigate = useNavigate();
    const storeSetUserType = useOnboardingStore((s) => s.setUserType);
    const [selected, setSelected] = useState<UserType | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const handleContinue = async () => {
        if (!selected) return;
        if (selected === 'CORPORATE') {
            setErr(t.corporateDisabled);
            return;
        }
        try {
            setSubmitting(true);
            setErr(null);
            await apiSetUserType(selected);
            storeSetUserType(selected);
            navigate('/onboarding/prepare');
        } catch (e) {
            setErr(e instanceof Error ? e.message : t.somethingWrong);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MobileFrame
            progress={(1 / 7) * 100}
            showBack
            stepLabel={tpl(t.stepOf, { current: 1, total: 7 })}
        >
            <div
                className="fade-in"
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px 24px 28px',
                }}
            >
                <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 8 }}>
                    {t.userTypeTitle}
                </h1>
                <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-500)', marginBottom: 28 }}>
                    {t.userTypeSubtitle}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                    <UserTypeCard
                        icon="user"
                        title={t.userIndividu}
                        desc={t.userIndividuDesc}
                        selected={selected === 'INDIVIDUAL'}
                        onClick={() => {
                            setSelected('INDIVIDUAL');
                            setErr(null);
                        }}
                    />
                    <UserTypeCard
                        icon="building"
                        title={t.userCorporate}
                        desc={t.userCorporateDesc}
                        selected={selected === 'CORPORATE'}
                        onClick={() => {
                            setSelected('CORPORATE');
                            setErr(t.corporateDisabled);
                        }}
                        disabled
                    />
                </div>

                {err && (
                    <div
                        className="chip chip-warning"
                        style={{ marginBottom: 12, alignSelf: 'flex-start' }}
                    >
                        <Icon name="alertTriangle" size={14} /> {err}
                    </div>
                )}

                <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    disabled={!selected || submitting || selected === 'CORPORATE'}
                    onClick={handleContinue}
                >
                    {submitting ? <div className="spinner" /> : t.continue}
                    {!submitting && <Icon name="arrowRight" size={18} />}
                </button>
            </div>
        </MobileFrame>
    );
}

interface UserTypeCardProps {
    icon: IconName;
    title: string;
    desc: string;
    selected: boolean;
    onClick: () => void;
    disabled?: boolean;
}

function UserTypeCard({ icon, title, desc, selected, onClick, disabled }: UserTypeCardProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                display: 'flex',
                gap: 14,
                alignItems: 'flex-start',
                textAlign: 'left',
                padding: '18px 16px',
                borderRadius: 14,
                background: selected ? 'var(--primary-soft)' : 'var(--surface)',
                border: `1.5px solid ${selected ? 'var(--primary)' : 'var(--ink-200)'}`,
                transition: 'all 0.2s var(--ease-out)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                width: '100%',
            }}
        >
            <div
                style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    flexShrink: 0,
                    background: selected ? 'var(--primary)' : 'var(--ink-100)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s',
                }}
            >
                <Icon name={icon} size={22} color={selected ? 'white' : 'var(--ink-700)'} />
            </div>
            <div style={{ flex: 1 }}>
                <div
                    style={{
                        fontSize: 16,
                        fontWeight: 600,
                        marginBottom: 2,
                        color: 'var(--ink-900)',
                    }}
                >
                    {title}
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--ink-500)' }}>
                    {desc}
                </div>
            </div>
            <div
                style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    flexShrink: 0,
                    marginTop: 11,
                    border: `2px solid ${selected ? 'var(--primary)' : 'var(--ink-300)'}`,
                    background: selected ? 'var(--primary)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s',
                }}
            >
                {selected && <Icon name="check" size={12} color="white" strokeWidth={3} />}
            </div>
        </button>
    );
}
